import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Resolver, useForm } from "react-hook-form"

import useDialogConfirmClose from "@hooks/useDialogConfirmClose"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import replaceSlugs from "@utils/replaceSlugs"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import DialogFooter from "@molecules/Dialog/DialogFooter/DialogFooter"
import DialogHeader from "@molecules/Dialog/DialogHeader/DialogHeader"
import Form from "@molecules/Form/Form"
import Input from "@molecules/Form/inputs/Input/Input"
import toast from "@molecules/Toast/Toast"
import { Dialog, Prompt } from "@molecules/index"

import {
    connectEcommerceStoreRequest,
    useInvalidateEcommerceConnections,
} from "@pages/Settings/SettingsEcommerce/SettingsEcommerce.store"
import {
    EcommerceApiErrorResponse,
    EcommerceConnectPayload,
} from "@pages/Settings/SettingsEcommerce/SettingsEcommerce.types"

import { ECOMMERCE_ENDPOINTS } from "@endpoints/ecommerce"

import styles from "./EcommerceConnectDialog.module.scss"
import { EcommerceConnectDialogProps, EcommerceConnectFormValues } from "./EcommerceConnectDialog.types"
import {
    applyConnectApiErrorsToForm,
    buildConnectDefaultValues,
    buildConnectFormSchema,
} from "./EcommerceConnectDialog.utils"

export default function EcommerceConnectDialog(props: EcommerceConnectDialogProps) {
    const { platform, connection = null, isOpen, onOpenChange } = props

    const isEditMode = connection !== null

    const { user } = useUser()
    const companyName = user?.service_company?.name ?? ""

    const [connectionsEndpoint, connectionDetailsEndpointTemplate] = useSlugExtractor([
        ECOMMERCE_ENDPOINTS.CONNECTIONS,
        ECOMMERCE_ENDPOINTS.CONNECTION_DETAILS,
    ])
    const invalidateConnections = useInvalidateEcommerceConnections()

    const [nonFieldError, setNonFieldError] = useState<string | null>(null)

    const defaultValues = useMemo(
        () => buildConnectDefaultValues(platform, connection, companyName),
        [platform, connection, companyName],
    )
    const formSchema = useMemo(() => buildConnectFormSchema(platform), [platform])

    const form = useForm<EcommerceConnectFormValues>({
        resolver: yupResolver(formSchema) as Resolver<EcommerceConnectFormValues>,
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues,
    })

    const {
        isDialogOpen,
        isPromptOpen,
        closeDialog,
        handleCloseAttempt,
        handleDialogOpenChange,
        handlePromptCancel,
        handlePromptDiscard,
    } = useDialogConfirmClose({
        isOpen,
        onOpenChange,
        shouldConfirmClose: () => form.formState.isDirty,
    })

    const connectMutation = useMutation<EcommerceConnection, EcommerceApiErrorResponse, EcommerceConnectPayload>({
        mutationFn: (payload) => {
            if (connection) {
                const connectionDetailsEndpoint = replaceSlugs(connectionDetailsEndpointTemplate, {
                    id: connection.id,
                })
                return connectEcommerceStoreRequest(connectionDetailsEndpoint, "PATCH", payload)
            }
            return connectEcommerceStoreRequest(connectionsEndpoint, "POST", payload)
        },
        onSuccess: (savedConnection) => {
            toast({
                type: "success",
                title: isEditMode
                    ? `${savedConnection.store_name} credentials updated`
                    : `${savedConnection.store_name} connected`,
            })
            closeDialog()
            void invalidateConnections()
        },
        onError: (error) => {
            applyConnectApiErrorsToForm(
                form,
                error,
                platform,
                isEditMode ? "Could not update the credentials" : "Could not connect the store",
                setNonFieldError,
            )
        },
    })

    // Whenever the dialog is closed, keep the form pinned to the target's current defaults so a
    // previous attempt's values and errors never leak through (and a refetched target reopens fresh).
    useEffect(() => {
        if (!isDialogOpen) {
            form.reset(defaultValues)
            setNonFieldError(null)
        }
    }, [isDialogOpen, defaultValues, form])

    // Clear the previous attempt's mutation state on close so a stale `isError` doesn't leak into the next opening.
    // Guard with a ref so we only reset on the open→closed transition, not on every re-render while closed.
    const wasDialogOpenRef = useRef(isDialogOpen)
    useEffect(() => {
        if (wasDialogOpenRef.current && !isDialogOpen) {
            connectMutation.reset()
        }
        wasDialogOpenRef.current = isDialogOpen
    }, [isDialogOpen, connectMutation])

    const customerIdValue = form.watch("customer_id")
    const hasCustomerId = Boolean(customerIdValue?.trim())

    const handleSubmit = useCallback(
        (values: EcommerceConnectFormValues) => {
            setNonFieldError(null)
            connectMutation.mutate({
                ...(isEditMode ? {} : { platform: platform.platform }),
                store_name: values.store_name,
                credentials: values.credentials,
                // Always sent: a blank customer id clears any bound customer back to guest checkout. The
                // company name rides along only as the binding confirmation and is ignored when blank.
                customer_id: values.customer_id ?? "",
                company_name: values.company_name ?? "",
            })
        },
        [connectMutation, isEditMode, platform.platform],
    )

    return (
        <>
            <Dialog.Root mode="dialog" variant="default" isOpen={isDialogOpen} onOpenChange={handleDialogOpenChange}>
                <Dialog.Content className={styles.dialogContent}>
                    <DialogHeader.Root>
                        <DialogHeader.Title
                            title={isEditMode ? "Update credentials" : `Connect ${platform.label} store`}
                            caption={connection?.store_name}
                        />
                        <DialogHeader.CloseButton onClick={handleCloseAttempt} />
                    </DialogHeader.Root>
                    <Form form={form} onSubmit={form.handleSubmit(handleSubmit) as () => void} className={styles.form}>
                        <Dialog.Body className={styles.body}>
                            {platform.required_scopes.length > 0 && (
                                <AlertBanner
                                    type="card"
                                    status="info"
                                    orientation="vertical"
                                    message={
                                        <>
                                            Ask the store admin to grant:
                                            <ul className={styles.scopesList}>
                                                {platform.required_scopes.map((scope) => (
                                                    <li key={scope}>{scope}</li>
                                                ))}
                                            </ul>
                                        </>
                                    }
                                />
                            )}
                            <Input
                                name="store_name"
                                label="Store name"
                                size="md"
                                autoFocus={true}
                                isRequired={true}
                                placeholder="Enter store name..."
                            />
                            {platform.credential_fields.map((credentialField) => (
                                <Input
                                    key={credentialField.name}
                                    name={`credentials.${credentialField.name}`}
                                    label={credentialField.label}
                                    size="md"
                                    type={credentialField.is_secret ? "password" : "text"}
                                    showVisibilityToggle={credentialField.is_secret}
                                    inputHelpText={credentialField.help_text || undefined}
                                    isRequired={true}
                                />
                            ))}
                            <Input
                                name="customer_id"
                                label="Store customer ID"
                                size="md"
                                isRequired={false}
                                placeholder="Optional"
                                inputHelpText="Attribute orders to a store customer so the store applies their pricing. Leave blank to order as a guest."
                            />
                            {hasCustomerId && (
                                <Input
                                    name="company_name"
                                    label="Your company name (as the store has it)"
                                    size="md"
                                    isRequired={true}
                                    inputHelpText="We confirm this matches the customer on file before attributing orders to them."
                                />
                            )}
                        </Dialog.Body>
                        {nonFieldError && (
                            <AlertBanner type="banner" status="error" message={nonFieldError} fullWidth={true} />
                        )}
                        <DialogFooter.Root>
                            <DialogFooter.LeftSide>
                                <Button variant="ghost" size="lg" onClick={handleCloseAttempt} noDebounce={true}>
                                    Cancel
                                </Button>
                            </DialogFooter.LeftSide>
                            <DialogFooter.RightSide>
                                <Button
                                    type="submit"
                                    variant="solid"
                                    size="lg"
                                    isProcessing={connectMutation.isPending}
                                    noDebounce={true}
                                >
                                    {isEditMode ? "Save" : "Connect"}
                                </Button>
                            </DialogFooter.RightSide>
                        </DialogFooter.Root>
                    </Form>
                </Dialog.Content>
            </Dialog.Root>

            <Prompt
                isOpen={isPromptOpen}
                title="Discard progress?"
                body="If you discard, your progress will not be saved."
                onCancel={handlePromptCancel}
                onConfirm={handlePromptDiscard}
                cancelText="Cancel"
                confirmText="Discard"
                isDangerousOperation={true}
            />
        </>
    )
}
